import { Action, type ActionEvent } from "~/analytics/Analytics";
import type { ProjectTemplateStatusAction } from "./projectTemplateStatusActions";
import { projectTemplateStatusActions } from "./projectTemplateStatusActions";

export type ProjectTemplateStatusState = {
    items: ProjectTemplateStatusItem[];
    indexOfActiveItem: number;
    projectTemplateInitiatedViaGuidedSetup: boolean;
    hasSteps: boolean;
    serverState?: ProjectTemplateServerState;
    hasLoadedServerState: boolean;
};

export type ProjectTemplateServerState = {
    HasSteps: boolean;
    HasPublishedVersions: boolean;
    HasSharedToSpaces: boolean;
};

export type ProjectTemplateStatusItem = {
    label: string;
    href?: string;
    showIndicator?: boolean;
    eventName?: string;
    eventAction?: ActionEvent;
};

export const projectTemplateStatusItems: Record<string, ProjectTemplateStatusItem> = {
    projectTemplate: { label: "Project Template", eventAction: { action: Action.Add, resource: "Project Template" } },
    process: { label: "Deployment Process", eventAction: { action: Action.Add, resource: "Deployment Process" } },
    publishAndShare: { label: "Publish & Share", eventAction: { action: Action.Add, resource: "Project Template" } },
};

export const initialProjectTemplateStatusState: ProjectTemplateStatusState = {
    items: [projectTemplateStatusItems.projectTemplate, projectTemplateStatusItems.process, projectTemplateStatusItems.publishAndShare],
    indexOfActiveItem: 0,
    projectTemplateInitiatedViaGuidedSetup: false,
    hasSteps: false,
    hasLoadedServerState: false,
};

export function projectTemplateStatusReducer(state: ProjectTemplateStatusState, action: ProjectTemplateStatusAction) {
    switch (action.type) {
        case projectTemplateStatusActions.projectTemplateStatusInitiated: {
            return {
                ...initialProjectTemplateStatusState,
                projectTemplateInitiatedViaGuidedSetup: true,
                hasLoadedServerState: true,
                serverState: {
                    HasSteps: false,
                    HasPublishedVersions: false,
                    HasSharedToSpaces: false,
                },
            };
        }

        case projectTemplateStatusActions.projectTemplateStatusCreated: {
            return {
                ...state,
                indexOfActiveItem: state.indexOfActiveItem + 1,
            };
        }

        case projectTemplateStatusActions.projectTemplateServerStateLoaded: {
            // When loading an existing template, mark the Project Template as complete
            // and calculate the active item index based on what's complete
            const { HasSteps, HasPublishedVersions, HasSharedToSpaces } = action.serverState;

            let newActiveItemIndex = 1; // Start at Deployment Process

            if (HasSteps) {
                newActiveItemIndex = 2; // Move to Publish & Share
            }

            // Only mark as complete if BOTH published AND shared
            if (HasPublishedVersions && HasSharedToSpaces) {
                newActiveItemIndex = 3;
            }

            return {
                ...state,
                indexOfActiveItem: newActiveItemIndex,
                hasSteps: HasSteps,
                serverState: action.serverState,
                hasLoadedServerState: true,
            };
        }

        case projectTemplateStatusActions.projectTemplateDeploymentProcessAdded: {
            return {
                ...state,
                indexOfActiveItem: state.indexOfActiveItem + 1,
                hasSteps: true,
            };
        }

        case projectTemplateStatusActions.projectTemplateStatusDismissed: {
            return { ...state };
        }

        default:
            return state;
    }
}
